import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';
import { ArrowLeft, Building2, Plus, Loader2, Save, Power, Plug } from 'lucide-react';

const VAZIA = {
  razao_social: '', nome_fantasia: '', cnpj: '', inscricao_municipal: '', inscricao_estadual: '',
  logradouro: '', numero: '', complemento: '', bairro: '', cidade: '', uf: 'SP', cep: '',
  codigo_municipio: '',
  cnae: '', codigo_servico: '', item_lc116: '', descricao_servico: '',
  codigo_ctn: '', codigo_nbs: '', cst_ibscbs: '', cclasstrib_ibscbs: '', ind_cod_ope: '',
  regime_tributario: 'Tributado', optante_simples: false, me_epp: false,
  natureza_operacao: 1, tipo_tributacao: '', data_adesao_simples: '',
  anexo_simples: '', regime_apuracao_sn: '',
  aliquota_iss: 0, iss_retido: false, exigibilidade_iss: '1',
  serie_nfse: '1', serie_rps: '1', proximo_numero: 1,
  ambiente: 'homologacao', municipio_webservice: '', codigo_usuario: '', codigo_contribuinte: '',
  email: '', telefone: '', categorias_atendidas: '', ativo: true,
};

const Campo = ({ label, children, dica }) => (
  <div className="space-y-1">
    <Label className="text-xs">{label}</Label>
    {children}
    {dica && <p className="text-[11px] text-muted-foreground">{dica}</p>}
  </div>
);

export default function FiscalEmpresas() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [empresas, setEmpresas] = useState([]);
  const [aberto, setAberto] = useState(false);
  const [salvando, setSalvando] = useState(false);
  const [editandoId, setEditandoId] = useState(null);
  const [form, setForm] = useState(VAZIA);

  const set = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

  const carregar = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/fiscal/empresas', { params: { incluir_inativas: true } });
      setEmpresas(data.empresas || []);
    } catch (e) {
      toast.error('Erro ao carregar empresas emissoras');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { carregar(); }, []);

  const abrirNova = () => { setEditandoId(null); setForm(VAZIA); setAberto(true); };

  const abrirEdicao = async (id) => {
    try {
      const { data } = await api.get(`/fiscal/empresas/${id}`);
      setForm({
        ...VAZIA, ...data,
        categorias_atendidas: (data.categorias_atendidas || []).join(', '),
        codigo_usuario: '', codigo_contribuinte: '',
      });
      setEditandoId(id);
      setAberto(true);
    } catch (e) {
      toast.error('Erro ao abrir empresa');
    }
  };

  const salvar = async () => {
    if (!form.razao_social.trim() || !form.cnpj.trim()) {
      toast.error('Razão social e CNPJ são obrigatórios');
      return;
    }
    setSalvando(true);
    const payload = {
      ...form,
      aliquota_iss: Number(form.aliquota_iss) || 0,
      proximo_numero: Number(form.proximo_numero) || 1,
      natureza_operacao: Number(form.natureza_operacao) || 1,
      categorias_atendidas: String(form.categorias_atendidas || '')
        .split(',').map(s => s.trim()).filter(Boolean),
    };
    Object.keys(payload).forEach(k => {
      if (payload[k] === '' || payload[k] === null) delete payload[k];
    });
    try {
      if (editandoId) {
        await api.put(`/fiscal/empresas/${editandoId}`, payload);
        toast.success('Empresa emissora atualizada');
      } else {
        await api.post('/fiscal/empresas', payload);
        toast.success('Empresa emissora criada');
      }
      setAberto(false);
      carregar();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao salvar empresa');
    } finally {
      setSalvando(false);
    }
  };

  const inativar = async (id) => {
    try {
      await api.delete(`/fiscal/empresas/${id}`);
      toast.success('Empresa inativada');
      carregar();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao inativar');
    }
  };

  const testar = async (id) => {
    try {
      const { data } = await api.post('/fiscal/prefeitura/testar-conexao', null,
        { params: { prestador_id: id } });
      if (data.status === 'online') toast.success(`Webservice online (${data.ambiente})`);
      else toast.error(data.requisito_faltante || data.mensagem || 'Falha no handshake');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Falha ao testar conexão');
    }
  };

  return (
    <div className="p-6 space-y-6" data-testid="fiscal-empresas">
      <div className="flex items-start gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate('/fiscal')} data-testid="empresas-voltar">
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div className="flex-1">
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Building2 className="w-6 h-6" />
            Empresas emissoras de NFS-e
          </h1>
          <p className="text-muted-foreground">
            Cada empresa tem credenciais, inscrição municipal, RPS, sequência, serviço,
            tributação, ISS e prefeitura próprios — nada é compartilhado.
          </p>
        </div>
        <Button onClick={abrirNova} data-testid="empresas-nova">
          <Plus className="w-4 h-4 mr-2" />
          Nova empresa
        </Button>
      </div>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg">Cadastradas ({empresas.length})</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="space-y-3">{[...Array(3)].map((_, i) => <Skeleton key={i} className="h-12" />)}</div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Empresa</TableHead>
                    <TableHead>CNPJ</TableHead>
                    <TableHead>IM</TableHead>
                    <TableHead>Regime</TableHead>
                    <TableHead>Série NFS-e / RPS</TableHead>
                    <TableHead>Ambiente</TableHead>
                    <TableHead>Credenciais</TableHead>
                    <TableHead className="text-right">Ações</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {empresas.map(e => (
                    <TableRow key={e.id} className={e.ativo === false ? 'opacity-50' : ''}>
                      <TableCell className="font-medium">
                        {e.nome_fantasia || e.razao_social}
                        <p className="text-[11px] text-muted-foreground">{e.razao_social}</p>
                      </TableCell>
                      <TableCell className="text-xs">{e.cnpj}</TableCell>
                      <TableCell className="text-xs">{e.inscricao_municipal || '-'}</TableCell>
                      <TableCell className="text-xs">
                        {e.optante_simples ? 'Simples Nacional' : (e.regime_tributario || '-')}
                        {e.me_epp && <span className="ml-1 rounded bg-slate-100 px-1 text-[10px]">ME/EPP</span>}
                      </TableCell>
                      <TableCell className="text-xs">{e.serie_nfse || '-'} / {e.serie_rps || '-'}</TableCell>
                      <TableCell className="text-xs uppercase">{e.ambiente || '-'}</TableCell>
                      <TableCell>
                        <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold uppercase ${
                          e.credenciais_configuradas ? 'bg-emerald-50 text-emerald-700' : 'bg-amber-100 text-amber-800'}`}>
                          {e.credenciais_configuradas ? 'ok' : 'faltando'}
                        </span>
                      </TableCell>
                      <TableCell className="text-right space-x-2 whitespace-nowrap">
                        <Button size="sm" variant="outline" onClick={() => testar(e.id)}
                          data-testid={`empresa-testar-${e.id}`}>
                          <Plug className="w-3 h-3" />
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => abrirEdicao(e.id)}
                          data-testid={`empresa-editar-${e.id}`}>
                          Editar
                        </Button>
                        {e.ativo !== false && (
                          <Button size="sm" variant="ghost" onClick={() => inativar(e.id)}
                            data-testid={`empresa-inativar-${e.id}`}>
                            <Power className="w-3 h-3" />
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={aberto} onOpenChange={setAberto}>
        <DialogContent className="max-w-4xl max-h-[88vh] overflow-y-auto" data-testid="empresa-form">
          <DialogHeader>
            <DialogTitle>{editandoId ? 'Editar empresa emissora' : 'Nova empresa emissora'}</DialogTitle>
          </DialogHeader>

          <div className="space-y-5">
            <div>
              <Label className="text-xs uppercase tracking-wide text-slate-500">Identificação</Label>
              <div className="mt-2 grid grid-cols-1 gap-3 md:grid-cols-3">
                <Campo label="Razão social *">
                  <Input value={form.razao_social} onChange={e => set('razao_social', e.target.value)}
                    data-testid="empresa-razao-social" />
                </Campo>
                <Campo label="Nome fantasia">
                  <Input value={form.nome_fantasia} onChange={e => set('nome_fantasia', e.target.value)}
                    data-testid="empresa-nome-fantasia" />
                </Campo>
                <Campo label="CNPJ *">
                  <Input value={form.cnpj} onChange={e => set('cnpj', e.target.value)}
                    data-testid="empresa-cnpj" />
                </Campo>
                <Campo label="Inscrição municipal">
                  <Input value={form.inscricao_municipal}
                    onChange={e => set('inscricao_municipal', e.target.value)}
                    data-testid="empresa-im" />
                </Campo>
                <Campo label="Inscrição estadual">
                  <Input value={form.inscricao_estadual}
                    onChange={e => set('inscricao_estadual', e.target.value)} />
                </Campo>
                <Campo label="Status">
                  <Select value={form.ativo ? 'ativo' : 'inativo'}
                    onValueChange={v => set('ativo', v === 'ativo')}>
                    <SelectTrigger data-testid="empresa-status"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="ativo">Ativo</SelectItem>
                      <SelectItem value="inativo">Inativo</SelectItem>
                    </SelectContent>
                  </Select>
                </Campo>
              </div>
            </div>

            <div>
              <Label className="text-xs uppercase tracking-wide text-slate-500">Endereço</Label>
              <div className="mt-2 grid grid-cols-1 gap-3 md:grid-cols-4">
                <Campo label="Logradouro">
                  <Input value={form.logradouro} onChange={e => set('logradouro', e.target.value)}
                    data-testid="empresa-logradouro" />
                </Campo>
                <Campo label="Número">
                  <Input value={form.numero} onChange={e => set('numero', e.target.value)} />
                </Campo>
                <Campo label="Complemento">
                  <Input value={form.complemento} onChange={e => set('complemento', e.target.value)} />
                </Campo>
                <Campo label="Bairro">
                  <Input value={form.bairro} onChange={e => set('bairro', e.target.value)} />
                </Campo>
                <Campo label="Município">
                  <Input value={form.cidade} onChange={e => set('cidade', e.target.value)}
                    data-testid="empresa-cidade" />
                </Campo>
                <Campo label="UF">
                  <Input maxLength={2} value={form.uf} onChange={e => set('uf', e.target.value.toUpperCase())} />
                </Campo>
                <Campo label="CEP">
                  <Input value={form.cep} onChange={e => set('cep', e.target.value)} />
                </Campo>
                <Campo label="Código IBGE do município">
                  <Input value={form.codigo_municipio} onChange={e => set('codigo_municipio', e.target.value)} />
                </Campo>
              </div>
            </div>

            <div>
              <Label className="text-xs uppercase tracking-wide text-slate-500">Serviço</Label>
              <div className="mt-2 grid grid-cols-1 gap-3 md:grid-cols-4">
                <Campo label="CNAE">
                  <Input value={form.cnae} onChange={e => set('cnae', e.target.value)} />
                </Campo>
                <Campo label="Código de serviço municipal">
                  <Input value={form.codigo_servico} onChange={e => set('codigo_servico', e.target.value)}
                    data-testid="empresa-codigo-servico" />
                </Campo>
                <Campo label="Item da LC 116">
                  <Input value={form.item_lc116} onChange={e => set('item_lc116', e.target.value)}
                    data-testid="empresa-item-lc116" />
                </Campo>
                <Campo label="Código CTN" dica="9 dígitos (layout 4.00)">
                  <Input value={form.codigo_ctn} onChange={e => set('codigo_ctn', e.target.value)} />
                </Campo>
                <Campo label="Código NBS" dica="9 dígitos (Anexo VIII)">
                  <Input value={form.codigo_nbs} onChange={e => set('codigo_nbs', e.target.value)} />
                </Campo>
                <Campo label="CST IBS/CBS">
                  <Input value={form.cst_ibscbs} onChange={e => set('cst_ibscbs', e.target.value)} />
                </Campo>
                <Campo label="cClassTrib">
                  <Input value={form.cclasstrib_ibscbs} onChange={e => set('cclasstrib_ibscbs', e.target.value)} />
                </Campo>
                <Campo label="Código da operação (IndCodOpe)">
                  <Input value={form.ind_cod_ope} onChange={e => set('ind_cod_ope', e.target.value)} />
                </Campo>
              </div>
              <div className="mt-3">
                <Campo label="Descrição do serviço">
                  <Textarea rows={2} value={form.descricao_servico}
                    onChange={e => set('descricao_servico', e.target.value)} />
                </Campo>
              </div>
            </div>

            <div>
              <Label className="text-xs uppercase tracking-wide text-slate-500">Regime tributário e ISS</Label>
              <div className="mt-2 grid grid-cols-1 gap-3 md:grid-cols-4">
                <Campo label="Regime tributário">
                  <Input value={form.regime_tributario} onChange={e => set('regime_tributario', e.target.value)}
                    data-testid="empresa-regime" />
                </Campo>
                <Campo label="Natureza da operação">
                  <Input type="number" value={form.natureza_operacao}
                    onChange={e => set('natureza_operacao', e.target.value)} />
                </Campo>
                <Campo label="Alíquota ISS (%)">
                  <Input type="number" step="0.01" value={form.aliquota_iss}
                    onChange={e => set('aliquota_iss', e.target.value)} data-testid="empresa-aliquota" />
                </Campo>
                <Campo label="Exigibilidade do ISS">
                  <Input value={form.exigibilidade_iss} onChange={e => set('exigibilidade_iss', e.target.value)} />
                </Campo>
                <Campo label="Tipo de tributação (TipoTrib)" dica="1=Tributado · 4=Simples Nacional">
                  <Input value={form.tipo_tributacao} onChange={e => set('tipo_tributacao', e.target.value)} />
                </Campo>
                <Campo label="Data de adesão ao Simples">
                  <Input type="date" value={String(form.data_adesao_simples || '').slice(0, 10)}
                    onChange={e => set('data_adesao_simples', e.target.value)} />
                </Campo>
                <Campo label="Anexo do Simples (SRV_ANEXOSN)"
                  dica="Anexo do CNAE no Simples: I, II, III, IV ou V">
                  <Input value={form.anexo_simples || ''}
                    onChange={e => set('anexo_simples', e.target.value)}
                    data-testid="empresa-anexo-simples" />
                </Campo>
                <Campo label="Regime de apuração do SN (RegApTribSN)"
                  dica="1=federais e municipais pelo SN · 2=ISSQN pela NFS-e · 3=tudo pela NFS-e">
                  <Input value={form.regime_apuracao_sn || ''}
                    onChange={e => set('regime_apuracao_sn', e.target.value)}
                    data-testid="empresa-regime-apuracao-sn" />
                </Campo>
              </div>
              <div className="mt-3 flex flex-wrap gap-6">
                <label className="flex items-center gap-2 text-sm">
                  <Checkbox checked={!!form.optante_simples}
                    onCheckedChange={v => set('optante_simples', !!v)} data-testid="empresa-simples" />
                  Optante pelo Simples Nacional
                </label>
                <label className="flex items-center gap-2 text-sm">
                  <Checkbox checked={!!form.me_epp} onCheckedChange={v => set('me_epp', !!v)}
                    data-testid="empresa-me-epp" />
                  ME / EPP
                </label>
                <label className="flex items-center gap-2 text-sm">
                  <Checkbox checked={!!form.iss_retido} onCheckedChange={v => set('iss_retido', !!v)}
                    data-testid="empresa-iss-retido" />
                  ISS retido na fonte
                </label>
              </div>
            </div>

            <div>
              <Label className="text-xs uppercase tracking-wide text-slate-500">RPS, numeração e prefeitura</Label>
              <div className="mt-2 grid grid-cols-1 gap-3 md:grid-cols-4">
                <Campo label="Série NFS-e">
                  <Input value={form.serie_nfse} onChange={e => set('serie_nfse', e.target.value)} />
                </Campo>
                <Campo label="Série do RPS" dica="Não pode ser igual à série da NFS-e">
                  <Input value={form.serie_rps} onChange={e => set('serie_rps', e.target.value)}
                    data-testid="empresa-serie-rps" />
                </Campo>
                <Campo label="Próximo número">
                  <Input type="number" value={form.proximo_numero}
                    onChange={e => set('proximo_numero', e.target.value)} />
                </Campo>
                <Campo label="Ambiente">
                  <Select value={form.ambiente} onValueChange={v => set('ambiente', v)}>
                    <SelectTrigger data-testid="empresa-ambiente"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="homologacao">Homologação</SelectItem>
                      <SelectItem value="producao">Produção</SelectItem>
                    </SelectContent>
                  </Select>
                </Campo>
                <Campo label="Município no webservice" dica="ex.: sp.taboaodaserra">
                  <Input value={form.municipio_webservice}
                    onChange={e => set('municipio_webservice', e.target.value)}
                    data-testid="empresa-municipio-ws" />
                </Campo>
                <Campo label="Código de usuário (prefeitura)" dica={editandoId ? 'Deixe vazio para manter o atual' : ''}>
                  <Input value={form.codigo_usuario} onChange={e => set('codigo_usuario', e.target.value)}
                    data-testid="empresa-codigo-usuario" />
                </Campo>
                <Campo label="Código de contribuinte" dica={editandoId ? 'Deixe vazio para manter o atual' : ''}>
                  <Input value={form.codigo_contribuinte}
                    onChange={e => set('codigo_contribuinte', e.target.value)}
                    data-testid="empresa-codigo-contribuinte" />
                </Campo>
                <Campo label="Token da API (se houver)">
                  <Input value={form.api_token || ''} onChange={e => set('api_token', e.target.value)} />
                </Campo>
              </div>
            </div>

            <div>
              <Label className="text-xs uppercase tracking-wide text-slate-500">Contato e escopo</Label>
              <div className="mt-2 grid grid-cols-1 gap-3 md:grid-cols-3">
                <Campo label="E-mail">
                  <Input value={form.email} onChange={e => set('email', e.target.value)} />
                </Campo>
                <Campo label="Telefone">
                  <Input value={form.telefone} onChange={e => set('telefone', e.target.value)} />
                </Campo>
                <Campo label="Categorias faturadas"
                  dica="Separe por vírgula. Vazio = todas. Define quais títulos são compatíveis.">
                  <Input value={form.categorias_atendidas}
                    onChange={e => set('categorias_atendidas', e.target.value)}
                    data-testid="empresa-categorias" />
                </Campo>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setAberto(false)}>Cancelar</Button>
            <Button onClick={salvar} disabled={salvando} data-testid="empresa-salvar">
              {salvando ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
              Salvar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
